import React, { lazy, type PointerEvent, type ReactNode, type RefObject, Suspense, useEffect, useId, useRef } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { type OverlayInjectedProps } from 'react-bootstrap/Overlay';
import { useTranslation } from 'react-i18next';
import {
  convertRefToUsfm,
  getFormattedRef,
  parseBibleViewOptions,
  serializeBibleViewOptions,
} from '@soulhx/fs-common';
import { type IPassageLink } from './PassageLink.js';
const BibleViewer = lazy(() => import('../viewer/BibleViewer.js'));

const PREVIEW_OPTIONS = serializeBibleViewOptions({
  ...parseBibleViewOptions(),
  footnotes: false,
  crossRef: false,
  headings: false,
  verseNums: false,
});

type IPassagePreviewPopover = Pick<IPassageLink, 'passage' | 'version' | 'context' | 'includeVerses'> & {
  /**
   * Element the popover is anchored to
   */
  target: RefObject<HTMLElement | null>;

  show: boolean;

  /**
   * Handlers from `useMousePreview`, so moving into the popover keeps it open
   */
  overlayHandlers: {
    onPointerEnter: (e: PointerEvent) => void;
    onPointerLeave: (e: PointerEvent) => void;
  };
};

/**
 * Calls `onResize` whenever its content changes size; e.g., when the lazy
 * `BibleViewer` replaces the loading placeholder.
 */
const ResizeWatcher = ({ onResize, children }: { onResize: () => void; children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver(onResize);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [onResize]);

  return <div ref={ref}>{children}</div>;
};

type IPreviewContent = Omit<IPassagePreviewPopover, 'target' | 'show'> & OverlayInjectedProps;

/**
 * Only mounted while the preview is shown, so links that are never hovered
 * don't pay for the hooks in here.
 */
const PreviewContent = ({
  overlayHandlers,
  passage,
  version,
  context,
  includeVerses,
  popper,
  ...overlayProps
}: IPreviewContent) => {
  const { t } = useTranslation(['bibleviewer']);
  const id = useId();
  const formattedRef = getFormattedRef(passage, { includeVerses, context });

  return (
    <Popover
      {...overlayProps}
      popper={popper}
      id={id}
      aria-label={t('bibleviewer:passagePreview.label', { ref: formattedRef })}
      style={{ ...overlayProps.style, maxWidth: '28rem', width: '28rem' }}
      {...overlayHandlers}
    >
      <Popover.Header>{formattedRef}</Popover.Header>
      <Popover.Body>
        {/* the popover grows once the passage loads, so it has to be re-positioned
            then; otherwise it can end up covering the link it was anchored to */}
        <ResizeWatcher onResize={() => popper.scheduleUpdate?.()}>
          <div
            style={{
              maxHeight: '12rem',
              overflow: 'hidden',
              maskImage: 'linear-gradient(to bottom, black 70%, transparent)',
            }}
          >
            <Suspense fallback=". . .">
              <BibleViewer
                passage={convertRefToUsfm(passage, { context })}
                version={version}
                options={PREVIEW_OPTIONS}
                hideOptions
                readOnly
                forceHideRef
              />
            </Suspense>
          </div>
          <div className="small text-body-secondary mt-1">{t('bibleviewer:passagePreview.hint')}</div>
        </ResizeWatcher>
      </Popover.Body>
    </Popover>
  );
};

/**
 * Truncated, read-only preview of a passage, shown when hovering a
 * `PassageLink` with a mouse.
 */
export const PassagePreviewPopover = ({ target, show, ...contentProps }: IPassagePreviewPopover) => (
  <Overlay target={target} show={show} placement="bottom" flip>
    {(overlayProps) => <PreviewContent {...contentProps} {...overlayProps} />}
  </Overlay>
);
